import type { RiskType, UpcomingPolicyStep } from "@unbox/api";
import { Activity, ChevronDown, ChevronUp, ShieldZap, XClose } from "@untitledui/icons";
import { useState } from "react";
import { QuarantineTag } from "@/components/assurance/badges/ad-campaign-badge";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { VideoDetails } from "@/components/video-details";
import { CreatorDetails } from "@/pages/creators/creator-details";
import type { ActivityItem } from "./ActivityFeed";
import { ActivityFeed } from "./ActivityFeed";
import { RiskBlock } from "./RiskBlock";
import { UpcomingPolicyActions } from "./UpcomingPolicyActions";

export type VideoDetailData = {
  id: string;
  title: string;
  creator: string;
  creatorId?: string;
  views: string;
  postedAgo: string;
  postedAtDate: string;
  urlFirstFrame?: string | null;
  isQuarantined?: boolean;
  riskDetails: {
    name: string;
    severity: "critical" | "high" | "medium" | "low";
    description?: string;
    evidenceType: RiskType;
    confirmedBy?: string;
    confirmedAt?: string;
    evidence: { ts?: string; text: string; translation?: string }[];
  }[];
  activity: ActivityItem[];
  upcomingSteps?: UpcomingPolicyStep[];
};

type VideoProgressDetailProps = {
  video: VideoDetailData;
  onClose: () => void;
  // True while the video's detail (upcomingSteps/activity) is still being fetched —
  // shows a skeleton in the Activity card instead of a misleading "No history yet".
  isLoadingActivity?: boolean;
};

export const VideoProgressDetail = ({ video, onClose, isLoadingActivity }: VideoProgressDetailProps) => {
  const [selectedVideoId, setSelectedVideoId] = useState<string | undefined>();
  const [selectedCreatorId, setSelectedCreatorId] = useState<string | undefined>();
  const [risksOpen, setRisksOpen] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(true);

  return (
    <div className="w-[calc(57%-0.5rem)] min-w-0 border border-border-primary rounded-md bg-white flex flex-col min-h-0 overflow-hidden max-h-full">
      {/* Header */}
      <div className="px-5 py-3.5 flex items-center gap-3.5 flex-wrap">
        <div
          className="relative shrink-0 w-[48px] h-[48px] rounded-sm overflow-hidden"
          style={!video.urlFirstFrame ? { background: "linear-gradient(135deg, #FFD6A5, #FF8FA3)" } : undefined}
        >
          {video.urlFirstFrame && (
            <img src={video.urlFirstFrame} alt={video.title} className="w-full h-full object-cover" />
          )}
        </div>
        <div className="flex-1 min-w-0 max-w-[60%]">
          <Tooltip title={video.title}>
            <TooltipTrigger
              className="inline-flex w-full min-w-0 justify-start"
              onPress={() => setSelectedVideoId(video.id)}
            >
              <span className="text-base font-medium underline decoration-dotted truncate cursor-pointer">
                {video.title}
              </span>
            </TooltipTrigger>
          </Tooltip>
          <button
            type="button"
            className="text-xs text-tertiary mt-0.5 underline decoration-dotted cursor-pointer hover:text-secondary transition-colors bg-transparent border-none p-0"
            onClick={() => video.creatorId && setSelectedCreatorId(video.creatorId)}
          >
            {video.creator}
          </button>
        </div>
        <div className="flex gap-6 items-center shrink-0">
          <div className="flex flex-col items-center">
            <span className="text-sm font-medium text-primary">{video.views}</span>
            <span className="text-sm text-tertiary">Views</span>
          </div>
          <div className="flex flex-col items-center">
            <span className="text-sm font-medium text-primary">{video.postedAtDate}</span>
            <span className="text-sm text-tertiary">Posted</span>
          </div>
          <div className="flex w-[24px] h-[36px]">
            <button
              type="button"
              aria-label="Close video detail"
              className="h-[10px] text-tertiary hover:text-secondary transition-colors bg-transparent border-none p-0 cursor-pointer"
              onClick={onClose}
            >
              <XClose className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col gap-6 p-4 overflow-y-auto bg-white flex-1">
        {/* Quarantine indicator */}
        {video.isQuarantined && <QuarantineTag />}
        {/* Confirmed risks card */}
        <Collapsible open={risksOpen} onOpenChange={setRisksOpen}>
          <div className="bg-[#fcf6f1] border-none flex flex-col gap-4 rounded-md p-3.5">
            <CollapsibleTrigger className="flex w-full items-center gap-4">
              <ShieldZap className="w-5 h-5 text-secondary" />
              <span className="text-sm font-medium text-secondary flex-1 text-left">Confirmed risks</span>
              {risksOpen ? (
                <ChevronUp className="w-5 h-5 text-secondary" />
              ) : (
                <ChevronDown className="w-5 h-5 text-secondary" />
              )}
            </CollapsibleTrigger>
            <CollapsibleContent>
              {isLoadingActivity ? (
                <Skeleton className="h-[120px] w-full rounded-md" />
              ) : (
                video.riskDetails.map((rd) => (
                  <RiskBlock
                    key={rd.name}
                    name={rd.name}
                    severity={rd.severity}
                    description={rd.description}
                    evidenceType={rd.evidenceType}
                    evidence={rd.evidence}
                    confirmedBy={rd.confirmedBy}
                    confirmedAt={rd.confirmedAt}
                  />
                ))
              )}
            </CollapsibleContent>
          </div>
        </Collapsible>

        {/* Activity card */}
        <Collapsible open={historyOpen} onOpenChange={setHistoryOpen}>
          <div className="bg-[#fcf6f1] border-none flex flex-col gap-4 rounded-md p-3.5">
            <CollapsibleTrigger className="flex w-full items-center gap-4">
              <Activity className="w-5 h-5 text-secondary" />
              <span className="text-sm font-medium text-secondary flex-1 text-left">Activity</span>
              {historyOpen ? (
                <ChevronUp className="w-5 h-5 text-secondary" />
              ) : (
                <ChevronDown className="w-5 h-5 text-secondary" />
              )}
            </CollapsibleTrigger>
            <CollapsibleContent>
              {isLoadingActivity ? (
                <Skeleton className="h-[56px] w-full rounded-md" />
              ) : (
                <>
                  <UpcomingPolicyActions key={video.id} steps={video.upcomingSteps ?? []} />
                  <ActivityFeed items={video.activity} />
                </>
              )}
            </CollapsibleContent>
          </div>
        </Collapsible>
      </div>

      <CreatorDetails id={selectedCreatorId} onClose={() => setSelectedCreatorId(undefined)} />
      <VideoDetails isAssurance={true} id={selectedVideoId} onClose={() => setSelectedVideoId(undefined)} />
    </div>
  );
};
